import { useEffect, useState } from "react";
import { GraduationCap, Globe2, FileBadge, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import axios from "axios";
import LegalBadgeCard from "@/components/site/LegalBadgeCard";

// Reasonable fallback so the section never looks empty even before the catalog
// API responds. These are the universities we seed by default.
const FALLBACK = [
  { country: "México", university_name: "UNAM — Universidad Nacional Autónoma de México", citation_style: "APA 7" },
  { country: "Argentina", university_name: "UBA — Universidad de Buenos Aires", citation_style: "Harvard" },
  { country: "Chile", university_name: "PUC — Pontificia Universidad Católica de Chile", citation_style: "Vancouver" },
];

const FLAG = {
  México: "🇲🇽",
  Argentina: "🇦🇷",
  Chile: "🇨🇱",
  Colombia: "🇨🇴",
  Perú: "🇵🇪",
  España: "🇪🇸",
};

export default function Universities() {
  const [items, setItems] = useState(FALLBACK);

  useEffect(() => {
    (async () => {
      try {
        const url = `${process.env.REACT_APP_BACKEND_URL}/api/catalog/public/universities`;
        const { data } = await axios.get(url);
        if (Array.isArray(data?.items) && data.items.length) setItems(data.items);
      } catch {
        /* keep fallback */
      }
    })();
  }, []);

  const byCountry = items.reduce((acc, u) => {
    (acc[u.country] = acc[u.country] || []).push(u);
    return acc;
  }, {});

  return (
    <section
      id="universidades"
      data-testid="section-universities"
      className="relative scroll-mt-24"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8 py-24 lg:py-32">
        <div className="max-w-2xl">
          <p className="text-[11px] tracking-[0.28em] uppercase text-copper font-medium">
            Universidades
          </p>
          <h2 className="mt-3 font-serif text-4xl sm:text-5xl text-ink leading-[1.05] text-balance">
            Plantillas oficiales para las normativas{" "}
            <span className="italic text-forest">más exigentes de LATAM</span>.
          </h2>
          <p className="mt-4 text-[15px] text-ink-soft max-w-xl leading-relaxed">
            Trabajamos con el manual real de cada institución: tipografía,
            márgenes, sangrías, citación e índice obligatorio. Si tu universidad
            no aparece, puedes subir su reglamento en PDF y configurarla en minutos.
          </p>
        </div>

        <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-5">
          {Object.entries(byCountry).map(([country, universities]) => (
            <article
              key={country}
              data-testid={`uni-country-${country}`}
              className="rounded-2xl border border-rule bg-white p-6 flex flex-col"
            >
              <div className="flex items-center gap-2">
                <span className="text-xl leading-none" aria-hidden="true">
                  {FLAG[country] || "🎓"}
                </span>
                <p className="text-[10px] tracking-[0.28em] uppercase text-copper font-medium">
                  {country}
                </p>
              </div>
              <ul className="mt-3 flex flex-col gap-3">
                {universities.map((u) => (
                  <li
                    key={u.university_name}
                    className="flex items-start gap-2.5"
                  >
                    <span className="mt-1 grid place-items-center h-7 w-7 shrink-0 rounded-lg bg-forest/10 text-forest">
                      <GraduationCap size={14} />
                    </span>
                    <div>
                      <p className="font-serif text-[14.5px] text-ink leading-snug">
                        {u.university_name}
                      </p>
                      <p className="mt-0.5 inline-flex items-center gap-1 text-[11px] text-ink-soft">
                        <FileBadge size={11} /> {u.citation_style}
                      </p>
                    </div>
                  </li>
                ))}
              </ul>
            </article>
          ))}
        </div>

        <div className="mt-12 rounded-2xl border border-copper/30 bg-[#FBF6EC] p-6 flex flex-col sm:flex-row items-start sm:items-center gap-4">
          <span className="grid place-items-center h-10 w-10 shrink-0 rounded-full bg-forest text-paper">
            <Globe2 size={16} className="text-[#E0C58A]" />
          </span>
          <div className="flex-1">
            <p className="font-serif text-lg text-ink leading-snug">
              ¿No ves tu universidad en la lista?
            </p>
            <p className="mt-1 text-[13px] text-ink-soft">
              Sube el reglamento de tu institución y el editor se adapta automáticamente.
              Si necesitas ayuda, escríbenos y configuramos la plantilla por ti.
            </p>
          </div>
          <Link
            to="/signup"
            data-testid="uni-cta"
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-[13px] font-medium text-paper bg-forest hover:bg-forest-dark"
          >
            Configurar mi universidad
            <ArrowRight size={13} />
          </Link>
        </div>

        {/* Metodología 100% Ética y Segura — moved here from Hero */}
        <div className="mt-12 max-w-3xl mx-auto">
          <LegalBadgeCard />
        </div>
      </div>
    </section>
  );
}
